import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import "./AppointmentBooking.css";
import image from "../../assets/about.jpg";

const AppointmentBooking = ({ id }) => {
  const { t } = useTranslation();
  const formRef = useRef();
  const formContainerRef = useRef();
  const imageContainerRef = useRef();
  const today = new Date().toISOString().split("T")[0];
  const [loading, setLoading] = useState(false);
  const [bookedTimes, setBookedTimes] = useState(() => {
    const saved = localStorage.getItem("bookedTimes");
    return saved ? JSON.parse(saved) : {};
  });
  const [selectedDate, setSelectedDate] = useState(today);
  const [isFormEmpty, setIsFormEmpty] = useState(true);
  const [showEmptyMessage, setShowEmptyMessage] = useState(false);

  useEffect(() => {
    localStorage.setItem("bookedTimes", JSON.stringify(bookedTimes));
  }, [bookedTimes]);

  useEffect(() => {
    const updateImageHeight = () => {
      if (formContainerRef.current && imageContainerRef.current) {
        const formHeight =
          formContainerRef.current.getBoundingClientRect().height;
        imageContainerRef.current.style.height = `${formHeight}px`;
      }
    };

    updateImageHeight();
    window.addEventListener("resize", updateImageHeight);

    return () => window.removeEventListener("resize", updateImageHeight);
  }, []);

  const serviceTranslations = {
    pediatric: t("appointment.service.pediatric"),
    orthodontics: t("appointment.service.orthodontics"),
    prosthodontics: t("appointment.service.prosthodontics"),
    endodontics: t("appointment.service.endodontics"),
    periodontics: t("appointment.service.periodontics"),
    surgery: t("appointment.service.surgery"),
    cosmetic: t("appointment.service.cosmetic"),
    implants: t("appointment.service.implants"),
    restorative: t("appointment.service.restorative"),
    preventive: t("appointment.service.preventive"),
    laser: t("appointment.service.laser"),
    emergency: t("appointment.service.emergency"),
  };

  const generateTimeOptions = (selectedDate) => {
    const times = [];
    let index = 0;
    for (let hour = 14; hour <= 22; hour++) {
      const period = t("appointment.time.pm");
      const displayHour = hour > 12 ? hour - 12 : hour;
      const time00 = `${displayHour}:00 ${period}`;
      const time30 = `${displayHour}:30 ${period}`;

      const isTime00Booked = bookedTimes[selectedDate]?.includes(time00);
      const isTime30Booked = bookedTimes[selectedDate]?.includes(time30);

      times.push(
        <option
          key={`time-${index++}`}
          value={time00}
          disabled={isTime00Booked}
        >
          {time00} {isTime00Booked ? `(${t("appointment.time.booked")})` : ""}
        </option>
      );
      times.push(
        <option
          key={`time-${index++}`}
          value={time30}
          disabled={isTime30Booked}
        >
          {time30} {isTime30Booked ? `(${t("appointment.time.booked")})` : ""}
        </option>
      );
    }
    return times;
  };

  const checkFormEmpty = () => {
    const formData = new FormData(formRef.current);
    const name = formData.get("name");
    const service = formData.get("service");
    const date = formData.get("date");
    const time = formData.get("time");
    const phone = formData.get("phone");

    const isEmpty = !name && !service && !date && !time && !phone;
    setIsFormEmpty(isEmpty);
  };

  const handleInputChange = () => {
    checkFormEmpty();
    setShowEmptyMessage(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(formRef.current);
    const name = formData.get("name");
    const service = formData.get("service");
    const date = formData.get("date");
    const time = formData.get("time");
    const phone = formData.get("phone");

    if (!name || !service || !date || !time || !phone) {
      alert(t("appointment.validation_error"));
      setLoading(false);
      return;
    }

    if (bookedTimes[date]?.includes(time)) {
      alert(t("appointment.time_already_booked"));
      setLoading(false);
      return;
    }

    setBookedTimes((prev) => ({
      ...prev,
      [date]: [...(prev[date] || []), time],
    }));

    const translatedData = {
      message_title: t("appointment.email.message_title", { name }),
      service_label: t("appointment.email.service_label"),
      service_translated: serviceTranslations[service] || service,
      date_label: t("appointment.email.date_label"),
      time_label: t("appointment.email.time_label"),
      phone_label: t("appointment.email.phone_label"),
      footer_message: t("appointment.email.footer_message"),
      name,
      date,
      time,
      phone,
    };

    emailjs
      .send(
        "service_rp8eaiu",
        "template_5cre44i",
        translatedData,
        "xLKmeKvkjY8-ttU2e"
      )
      .then(
        (result) => {
          console.log("Message Sent ✅", result.text);
          alert(t("appointment.success"));
          setLoading(false);
          setIsFormEmpty(true);
          setShowEmptyMessage(false);
          formRef.current.reset();
        },
        (error) => {
          console.log("Send Error ❌", error.text);
          alert(t("appointment.error"));
          setBookedTimes((prev) => ({
            ...prev,
            [date]: prev[date].filter((t) => t !== time),
          }));
          setLoading(false);
        }
      );
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    handleInputChange();
  };

  return (
    <section className="appointment-section" id={id}>
      <div className="appointment-container">
        <div className="appointment-form" ref={formContainerRef}>
          <h2 className="form-title">{t("appointment.title")}</h2>
          {showEmptyMessage && isFormEmpty && (
            <p className="empty-message">{t("appointment.validation_error")}</p>
          )}
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            onChange={handleInputChange}
            onFocus={handleInputChange}
          >
            <div className="form-row">
              <select
                name="service"
                className="form-input"
                defaultValue=""
                required
              >
                <option value="" disabled hidden>
                  {t("appointment.service.placeholder")}
                </option>
                {Object.keys(serviceTranslations).map((key) => (
                  <option key={key} value={key}>
                    {serviceTranslations[key]}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <input
                name="date"
                type="date"
                className="form-input date-input"
                min={today}
                onChange={handleDateChange}
                required
              />
              <select
                name="time"
                className="form-input time-input"
                defaultValue=""
                required
              >
                <option value="" disabled hidden>
                  {t("appointment.time.placeholder")}
                </option>
                {generateTimeOptions(selectedDate)}
              </select>
            </div>

            <div className="form-row">
              <input
                name="name"
                type="text"
                className="form-input name-input"
                placeholder={t("appointment.name.placeholder")}
                required
              />
              <input
                name="phone"
                type="tel"
                className="form-input phone-input"
                placeholder={t("appointment.phone.placeholder")}
                required
              />
            </div>

            <button type="submit" className="form-button" disabled={loading}>
              {loading ? t("appointment.sending") : t("appointment.button")}
            </button>
          </form>
        </div>

        <div className="appointment-image" ref={imageContainerRef}>
          <img
            src={image}
            alt={t("appointment.imageAlt")}
            className="image-photo"
          />
          <div className="image-text">
            <FontAwesomeIcon icon={faClock} className="clock-icon" />
            <div className="opening">
              <p className="opening-hours">
                <span>{t("appointment.openingHours.title")}</span>
              </p>
              <p className="hours-text">
                {t("appointment.openingHours.mondayFriday")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppointmentBooking;
